import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Card, Button, Input, Badge, Icon } from '../../components/ui';

export default function StripePanel() {
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  useEffect(() => {
    loadStripeConfig();
  }, []);

  const loadStripeConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_config')
        .select('stripe_publishable_key')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading Stripe config:', error);
        return;
      }

      if (data) {
        setHasExistingConfig(true);
        setPublishableKey(data.stripe_publishable_key || '');
      }
    } catch (err) {
      console.error('Error in loadStripeConfig:', err);
    }
  };

  const handleSave = async () => {
    if (!secretKey.trim()) {
      setSaveMessage('Please enter a Stripe secret key');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (!secretKey.startsWith('sk_')) {
      setSaveMessage('Invalid Stripe secret key format. It should start with "sk_"');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      const { data: existingConfig } = await supabase.from('stripe_config').select('id').maybeSingle();

      if (existingConfig) {
        const { error } = await supabase
          .from('stripe_config')
          .update({
            stripe_secret_key: secretKey,
            stripe_publishable_key: publishableKey || null,
            updated_at: new Date().toISOString(),
            updated_by: user.id,
          })
          .eq('id', existingConfig.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('stripe_config').insert({
          stripe_secret_key: secretKey,
          stripe_publishable_key: publishableKey || null,
          updated_by: user.id,
        });

        if (error) throw error;
      }

      setSaveMessage('Stripe configuration saved successfully!');
      setHasExistingConfig(true);
      setSecretKey('');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Error saving Stripe config:', err);
      setSaveMessage(err instanceof Error ? err.message : 'Failed to save Stripe configuration');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!secretKey.trim() && !hasExistingConfig) {
      setTestResult({
        success: false,
        message: 'Please save a Stripe secret key first',
      });
      setTimeout(() => setTestResult(null), 5000);
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const testKey = secretKey.trim() || null;

      let keyToTest = testKey;

      if (!keyToTest) {
        const { data } = await supabase.from('stripe_config').select('stripe_secret_key').single();

        keyToTest = data?.stripe_secret_key ?? null;
      }

      if (!keyToTest) {
        throw new Error('No Stripe key available to test');
      }

      const response = await fetch('https://api.stripe.com/v1/balance', {
        headers: {
          Authorization: `Bearer ${keyToTest}`,
        },
      });

      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Connection successful! Your Stripe API key is valid.',
        });
      } else {
        const errorData = await response.json();
        setTestResult({
          success: false,
          message: errorData.error?.message || 'Failed to connect to Stripe',
        });
      }
    } catch (err) {
      console.error('Error testing Stripe connection:', err);
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test Stripe connection',
      });
    } finally {
      setIsTesting(false);
      setTimeout(() => setTestResult(null), 5000);
    }
  };

  return (
    <Card
      eyebrow="Stripe"
      title="Connection"
      actions={
        hasExistingConfig ? (
          <Badge tone="up" dot>
            Connected
          </Badge>
        ) : (
          <Badge tone="idle" dot>
            Not Connected
          </Badge>
        )
      }
    >
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-4)' }}>
        {hasExistingConfig ? (
          publishableKey ? (
            <>
              Publishable key{' '}
              <span className="t-num" style={{ color: 'var(--text-body)', wordBreak: 'break-all' }}>
                {publishableKey}
              </span>
            </>
          ) : (
            'Secret key configured. Transactions sync from this account.'
          )
        ) : (
          'Add your Stripe API credentials to sync transaction data.'
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          padding: 'var(--space-4) 0',
          borderTop: '1px solid var(--border-hairline)',
          borderBottom: '1px solid var(--border-hairline)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div>
          <div className="flex items-end" style={{ gap: '8px' }}>
            <Input
              label="Secret Key"
              type={showSecretKey ? 'text' : 'password'}
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={hasExistingConfig ? '••••••••••••••••' : 'sk_live_...'}
            />
            <button
              type="button"
              className="pressable hover-wash"
              title={showSecretKey ? 'Hide key' : 'Show key'}
              onClick={() => setShowSecretKey(!showSecretKey)}
              style={{
                height: 'var(--control-md)',
                width: 'var(--control-md)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface-raised)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              {showSecretKey ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <p className="t-num" style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Starts with sk_
          </p>
        </div>

        <div>
          <Input
            label="Publishable Key"
            type="text"
            value={publishableKey}
            onChange={(e) => setPublishableKey(e.target.value)}
            placeholder="pk_live_..."
          />
          <p className="t-num" style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Optional · starts with pk_
          </p>
        </div>
      </div>

      {saveMessage && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 'var(--space-4)',
            padding: '9px 12px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            background: saveMessage.includes('success') ? 'var(--status-up-soft)' : 'var(--status-down-soft)',
            border: saveMessage.includes('success')
              ? '1px solid rgba(86, 162, 104, 0.25)'
              : '1px solid rgba(229, 72, 77, 0.25)',
            color: saveMessage.includes('success') ? 'var(--status-up)' : 'var(--danger-400)',
          }}
        >
          {saveMessage}
        </div>
      )}

      {testResult && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 'var(--space-4)',
            padding: '9px 12px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            background: testResult.success ? 'var(--status-up-soft)' : 'var(--status-down-soft)',
            border: testResult.success
              ? '1px solid rgba(86, 162, 104, 0.25)'
              : '1px solid rgba(229, 72, 77, 0.25)',
            color: testResult.success ? 'var(--status-up)' : 'var(--danger-400)',
          }}
        >
          <Icon name={testResult.success ? 'check' : 'circle-alert'} size={15} />
          {testResult.message}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <Button size="sm" icon="check" disabled={isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
        <Button size="sm" variant="secondary" icon="zap" disabled={isTesting} onClick={handleTestConnection}>
          {isTesting ? 'Testing...' : 'Test Connection'}
        </Button>
      </div>
    </Card>
  );
}
